// components/navbar/BottomNav.tsx
import React, { useCallback, useEffect, useRef } from "react";
import "./BottomNav.css";
import useHaptics from "../../hooks/useHaptics";
import { RouteConfig } from "../../routes/routes";

const FAB_GAP_PX = 0;

interface BottomNavProps {
  tabs: RouteConfig[];
  onNavigate?: (tab: string) => void;
  show?: boolean;
  activeTab: string;
}

// Create icon cache outside component - persists across all renders
const iconCache = new Map<string, React.ComponentType<any>>();

const BottomNav: React.FC<BottomNavProps> = ({
  tabs,
  onNavigate,
  show = true,
  activeTab,
}) => {
  const { buttonTap } = useHaptics();
  const navRef = useRef<HTMLElement | null>(null);

  // Cache icons on first encounter
  tabs.forEach(tab => {
    if (tab.icon && !iconCache.has(tab.name)) {
      iconCache.set(tab.name, tab.icon);
    }
  });

  // Publish the nav's actual top edge to a CSS variable. Any floating
  // element that needs to sit above the BottomNav should read
  // `var(--bottom-nav-clear)` — it stays accurate across 3-button nav,
  // gesture pill, hidden gesture bar, orientation changes, and
  // keyboard-driven inset changes, because ResizeObserver fires for
  // every layout change (which formulas using env() alone miss).
  useEffect(() => {
    const el = navRef.current;
    const root = document.documentElement;
    if (!el) {
      root.style.removeProperty("--bottom-nav-clear");
      return;
    }

    const publishPosition = () => {
      const rect = el.getBoundingClientRect();
      const distFromViewportBottom = window.innerHeight - rect.top;
      root.style.setProperty(
        "--bottom-nav-clear",
        `${Math.round(distFromViewportBottom + FAB_GAP_PX)}px`
      );
    };

    publishPosition();

    const ro = new ResizeObserver(publishPosition);
    ro.observe(el);
    window.addEventListener("resize", publishPosition);
    window.addEventListener("orientationchange", publishPosition);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", publishPosition);
      window.removeEventListener("orientationchange", publishPosition);
      root.style.removeProperty("--bottom-nav-clear");
    };
  }, [show]);

  const handleTabClick = useCallback(
    (tabName: string) => {
      if (activeTab === tabName) return;

      buttonTap();
      onNavigate?.(tabName);
    },
    [activeTab, buttonTap, onNavigate]
  );

  if (!show) return null;

  return (
    <nav
      ref={navRef}
      className="bottom-nav bg-white"
      aria-label="Main navigation"
    >
      {tabs.map((tab) => {
        const isActive = activeTab === tab.name;
        const IconComponent = iconCache.get(tab.name);
        
        if (!IconComponent) return null;
        
        return (
          <button
            key={tab.name}
            className={`nav-button ${isActive ? "active" : ""}`}
            onClick={() => handleTabClick(tab.name)}
            type="button"
            aria-label={tab.name}
            aria-current={isActive ? "page" : undefined}
          >
            <IconComponent
              className={isActive ? "active-icon" : "inactive-icon"}
              aria-hidden="true"
              width={isActive ? 32 : 40}
              height={isActive ? 32 : 40}
            />
          </button>
        );
      })}
    </nav>
  );
};

export default React.memo(BottomNav, (prev, next) => {
  // Only re-render if activeTab changes
  return prev.activeTab === next.activeTab && prev.show === next.show;
});